import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { groups as groupsApi, type DeviceSummary, type Group } from "../lib/api.js";
import { buildDeviceTree, subtreeDevices, type TreeNode } from "../lib/device-tree.js";
import { displayStatus, relativeTime } from "../lib/freshness.js";
import { StatusBadge } from "./StatusBadge.js";

/**
 * The device tree: organization > whatever you like > devices.
 *
 * Collapsing hides things, which on a monitoring screen is a hazard rather
 * than a convenience -- so a collapsed branch reports how many devices are
 * inside it and how many of those are not healthy. You can fold a site away
 * without folding away the fact that something in it is down.
 */

interface Props {
  groups: Group[];
  devices: DeviceSummary[];
  now: number;
  canManage: boolean;
  onChanged: () => void;
}

export function DeviceTree({ groups, devices, now, canManage, onChanged }: Props) {
  const { roots, unfiled, shared } = buildDeviceTree(groups, devices);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  return (
    <div style={{ fontSize: 13 }}>
      {error && <p style={{ color: "var(--status-critical)", fontSize: 12 }}>{`!! ${error}`}</p>}

      {roots.map((node) => (
        <GroupNode
          key={node.group.id}
          node={node}
          depth={0}
          now={now}
          canManage={canManage}
          collapsed={collapsed}
          onToggle={toggle}
          onChanged={onChanged}
          onError={setError}
        />
      ))}

      {/* Both buckets are only rendered when they have something in them, so
          an ordinary organization never sees a heading explaining a case it
          does not have. */}
      {shared.length > 0 && (
        <Bucket
          label="SHARED WITH US"
          hint="Owned by another organization."
          devices={shared}
          now={now}
        />
      )}

      {unfiled.length > 0 && (
        <Bucket
          label="NOT FILED"
          hint="Not in a group. Edit a device to file it."
          devices={unfiled}
          now={now}
        />
      )}
    </div>
  );
}

function GroupNode({
  node,
  depth,
  now,
  canManage,
  collapsed,
  onToggle,
  onChanged,
  onError,
}: {
  node: TreeNode<Group, DeviceSummary>;
  depth: number;
  now: number;
  canManage: boolean;
  collapsed: Record<string, boolean>;
  onToggle: (id: string) => void;
  onChanged: () => void;
  onError: (message: string | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const isCollapsed = collapsed[node.group.id] ?? false;
  const isRoot = node.group.parent_id === null;

  const inside = subtreeDevices(node);
  const unhealthy = inside.filter((device) => {
    const status = displayStatus(device, now);
    return status !== "ok" && status !== "never";
  }).length;

  const remove = async () => {
    if (!confirm(`Delete "${node.group.name}"?`)) return;
    onError(null);
    try {
      await groupsApi.remove(node.group.id);
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not delete");
    }
  };

  return (
    <div style={{ marginLeft: depth === 0 ? 0 : 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          padding: "4px 0",
          borderBottom: depth === 0 ? "1px solid var(--gridline)" : undefined,
        }}
      >
        <button onClick={() => onToggle(node.group.id)} style={plainButton}>
          <span style={{ color: "var(--text-muted)" }}>{isCollapsed ? "[+]" : "[-]"}</span>{" "}
          <span style={{ color: "var(--text-primary)" }}>{node.group.name}</span>
        </button>

        {node.group.kind && node.group.kind !== "organization" && (
          <span style={{ color: "var(--text-muted)", fontSize: 10, letterSpacing: "0.08em" }}>
            {node.group.kind.toUpperCase()}
          </span>
        )}

        {/* The count is always shown; the fault count only when non-zero, so
            it reads as an exception rather than a field that is usually 0. */}
        <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
          {inside.length} device{inside.length === 1 ? "" : "s"}
        </span>
        {unhealthy > 0 && (
          <span style={{ color: "var(--status-critical)", fontSize: 11 }}>
            {unhealthy} not ok
          </span>
        )}

        {canManage && (
          <span style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
            <button onClick={() => setAdding(true)} style={tinyButton}>[ + GROUP ]</button>{" "}
            {!isRoot && (
              <>
                <button onClick={() => setRenaming(true)} style={tinyButton}>[ RENAME ]</button>{" "}
                <button onClick={() => void remove()} style={{ ...tinyButton, color: "var(--status-critical)" }}>
                  [ DEL ]
                </button>
              </>
            )}
          </span>
        )}
      </div>

      {(adding || renaming) && (
        <GroupForm
          parentId={adding ? node.group.id : null}
          group={renaming ? node.group : null}
          onDone={() => { setAdding(false); setRenaming(false); onChanged(); }}
          onCancel={() => { setAdding(false); setRenaming(false); }}
          onError={onError}
        />
      )}

      {!isCollapsed && (
        <div style={{ borderLeft: "1px solid var(--gridline)", marginLeft: 6, paddingLeft: 10 }}>
          {node.devices.map((device) => (
            <DeviceRow key={device.id} device={device} now={now} />
          ))}

          {node.children.map((child) => (
            <GroupNode
              key={child.group.id}
              node={child}
              depth={depth + 1}
              now={now}
              canManage={canManage}
              collapsed={collapsed}
              onToggle={onToggle}
              onChanged={onChanged}
              onError={onError}
            />
          ))}

          {node.devices.length === 0 && node.children.length === 0 && (
            <div style={{ color: "var(--text-muted)", fontSize: 11, padding: "3px 0" }}>empty</div>
          )}
        </div>
      )}
    </div>
  );
}

function DeviceRow({ device, now }: { device: DeviceSummary; now: number }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "3px 0" }}>
      <StatusBadge status={displayStatus(device, now)} />
      <Link to={`/devices/${device.id}`} style={{ color: "var(--text-primary)" }}>
        {device.name}
      </Link>
      <span style={{ color: "var(--text-secondary)", fontSize: 11 }}>{device.address}</span>
      {device.is_shared && (
        <span style={{ color: "var(--text-muted)", fontSize: 10 }}>
          {`shared by ${device.owner_name ?? "another org"}`}
        </span>
      )}
      <span style={{ color: "var(--text-muted)", fontSize: 11, marginLeft: "auto" }}>
        {relativeTime(device.last_poll_at, now)}
      </span>
    </div>
  );
}

function Bucket({
  label,
  hint,
  devices,
  now,
}: {
  label: string;
  hint: string;
  devices: DeviceSummary[];
  now: number;
}) {
  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 8 }}>
      <div style={{ color: "var(--text-secondary)", fontSize: 11, letterSpacing: "0.1em" }}>
        {label}
      </div>
      <div style={{ color: "var(--text-muted)", fontSize: 10, marginBottom: 4 }}>{hint}</div>
      <div style={{ paddingLeft: 10 }}>
        {devices.map((device) => (
          <DeviceRow key={device.id} device={device} now={now} />
        ))}
      </div>
    </div>
  );
}

function GroupForm({
  parentId,
  group,
  onDone,
  onCancel,
  onError,
}: {
  parentId: string | null;
  group: Group | null;
  onDone: () => void;
  onCancel: () => void;
  onError: (message: string | null) => void;
}) {
  const [name, setName] = useState(group?.name ?? "");
  const [kind, setKind] = useState(group?.kind ?? "site");
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    onError(null);
    try {
      if (group) await groupsApi.update(group.id, { name, kind });
      else await groupsApi.create({ name, kind, parent_id: parentId! });
      onDone();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", gap: 6, alignItems: "center", padding: "4px 0 4px 16px" }}>
      <input
        autoFocus
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        style={input}
      />
      {/* Free text, not a fixed list: "site" and "rack" are the common cases,
          but a cabinet, a shelter or a pole is just as valid a container. */}
      <input
        value={kind ?? ""}
        onChange={(e) => setKind(e.target.value)}
        placeholder="site / rack / …"
        list="group-kinds"
        style={{ ...input, width: 110 }}
      />
      <datalist id="group-kinds">
        <option value="site" />
        <option value="rack" />
        <option value="building" />
        <option value="cabinet" />
      </datalist>
      <button type="submit" disabled={saving} style={tinyButton}>
        {saving ? "[ ... ]" : group ? "[ SAVE ]" : "[ ADD ]"}
      </button>
      <button type="button" onClick={onCancel} style={tinyButton}>[ X ]</button>
    </form>
  );
}

const plainButton: React.CSSProperties = {
  background: "none", border: "none", padding: 0, font: "inherit",
  cursor: "pointer", textTransform: "none", letterSpacing: "inherit",
};
const tinyButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "2px 6px",
  fontSize: 10, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.06em",
};
const input: React.CSSProperties = {
  padding: "4px 7px", fontSize: 12, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)",
};
